import Vue from "vue";
import Component from "vue-class-component";
import { Prop, Watch } from "vue-property-decorator";
import Axios from "axios";
import FormHelper from "@Scripts/Common/FormHelper";
import GoogleMapsHelper from "@Scripts/Infrastructure/GoogleMapsHelper";

@Component({ name: "WorkWithUsLocator" })
export default class WorkWithUsLocator extends Vue {
    public googleMapsHelper: any = GoogleMapsHelper;
    public isLoading: boolean = true;
    public isLoadingList: boolean = true;
    public showMap: boolean = false;
    public isCreated: boolean = true;
    public filterData: any = {
        topRightLatitude: null,
        topRightLongitude: null,
        bottomLeftLatitude: null,
        bottomLeftLongitude: null,
        searchtext: null,
        workingArea: null,
        offerType: null
    };
    public dealerPoints: any[] = [];
    public dealerJobOffers: any[] = [];
    public dealerJobOfferWorkingAreas: any[] = [];
    public dealerJobOfferOfferTypes: any[] = [];
    public validationErrors: any[] = [];
    public mapErrors: any[] = [];
    public selectedJobOffer: string = null;

    public suggestionIndex: number = -1;
    public suggestions: any[] = [];
    public debounceTimeOut: any = null;

    get routes() {
        return (<any>window).App.routes;
    }

    @Prop() readonly recaptchaSiteKey: string;

    @Watch("$route") async onUrlChange() {
        this.checkRouteParams();
    }

    public async created() {
        const self = this;

        await Promise.all([this.googleMapsHelper.loadMap(), this.getDealerJobOfferFormList()]);

        await this.googleMapsHelper.mapInstance.addListener("tilesloaded", async () => {
            do { await self.getJobOffers(); } while (self.isCreated && self.dealerJobOffers.length === 0);
        });

        if (this.$route.name === "JobOfferDetail") {
            const response = (await Axios.post(this.routes.WorkWithUs_GetDealerByJobOffer, { offerIdMaster: this.$route.params.offerIdMaster })).data;
            this.centerMarker(response);
        } else if (this.$route.query.dealerPointCode) {
            await this.getDealerPoints(<string>this.$route?.query?.dealerPointCode);

            if (this.dealerPoints.some(x => x.code === this.$route.query.dealerPointCode)) {
                this.centerMarker(this.dealerPoints.filter(x => x.code === this.$route.query.dealerPointCode)[0]);
            }
        }

        this.isLoading = false;
    }

    public mounted() {
        const outsideClickListener = event => {
            if (!document.getElementById("AutocompleteLocation").contains(event.target)) {
                this.filterData.searchtext = null;
            }
        }

        document.addEventListener("click", outsideClickListener);
        this.checkRouteParams();
    }

    public async resetFilter() {
        this.filterData.workingArea = null;
        this.filterData.offerType = null;
        await this.getJobOffers();
    }

    private checkRouteParams() {
        if (this.$route.name === "JobProfileSubmit" || this.$route.name === "JobOfferDetail") {
            this.selectedJobOffer = this.$route.params.offerIdMaster;
        } else {
            this.selectedJobOffer = null;
        }
    }

    public async getDealerPoints(dealerPointCode: string = null) {
        this.filterData.dealerPointCode = dealerPointCode ? dealerPointCode : null;
        const response = (await Axios.post(this.routes.WorkWithUs_GetDealerPointsByLocation, this.filterData)).data;
        this.dealerPoints = response;
    }

    public async getDealerJobOfferFormList() {
        const response = (await Axios.post(this.routes.WorkWithUs_GetDealerJobOfferFormList)).data;
        this.dealerJobOfferWorkingAreas = response.dealerJobOfferWorkingAreas;
        this.dealerJobOfferOfferTypes = response.dealerJobOfferOfferTypes;
    }

    public async getJobOffers() {
        let mapBounds = this.googleMapsHelper.getBounds();

        let nePoint = mapBounds.getNorthEast();
        let swPoint = mapBounds.getSouthWest();

        this.filterData.topRightLatitude = nePoint.lat();
        this.filterData.topRightLongitude = nePoint.lng();
        this.filterData.bottomLeftLatitude = swPoint.lat();
        this.filterData.bottomLeftLongitude = swPoint.lng();

        this.isLoadingList = true;

        const response = (await Axios.post(this.routes.WorkWithUs_GetJobOffers, this.filterData)).data;
        if (this.isCreated) {
            if (response && response.dealerOffers.length !== 0) {
                this.isCreated = false;
            } else if (this.googleMapsHelper.mapInstance.zoom !== this.googleMapsHelper.mapInstance.minZoom) {
                this.googleMapsHelper.mapInstance.setZoom(this.googleMapsHelper.mapInstance.zoom - 1);
            } else if (this.googleMapsHelper.mapInstance.zoom === this.googleMapsHelper.mapInstance.minZoom) {
                this.isCreated = false;
            }
        }

        this.isLoadingList = false;

        this.googleMapsHelper.removeMarkers();
        this.dealerJobOffers = response.dealerOffers;
        this.dealerPoints = response.dealerPoints;

        for (const item of this.dealerPoints.filter(x => this.dealerJobOffers.some(y => x.code === y.dealerPointCode))) {
            let jobOfferCount = this.dealerJobOffers.filter(y => item.code === y.dealerPointCode).length;

            let description = "<div class=\"pos-info\"><div class=\"data\"><div class=\"maintitle s16\">" + item.company + "</div><div class=\"address\">" + item.address + " " + item.location + " " + item.postCode + "</div><div class=\"position\">";

            if (jobOfferCount === 1) {
                description += "<a href=\"lavora-con-noi?dealerPointCode=" + item.code + "\"><span class=\"label\">1 posizione aperta</span></a></div></div>";
            } else {
                description += "<a href=\"lavora-con-noi?dealerPointCode=" + item.code + "\"><span class=\"label\">" + jobOfferCount + " posizioni aperte</span></a></div></div>";
            }

            description += "</div>";

            this.googleMapsHelper.addMarker(item.idMaster, item.latitude, item.longitude, item.company, description);
        }

        this.googleMapsHelper.setMarkerCluster();
    }

    public onSearchtextChanged() {
        this.debounceSearchText(500);
    }

    public async debounceSearchText(delay: number) {
        const self = this;
        clearTimeout(this.debounceTimeOut);
        this.debounceTimeOut = setTimeout(async function () {
            if (self.filterData.searchtext && self.filterData.searchtext.length >= 3) {
                await self.getSuggestions();
            } else {
                self.suggestions = [];
                self.suggestionIndex = -1;
            }
        }, delay, this);
    }

    public async getSuggestions() {
        const self = this;

        let autocompleteService = this.googleMapsHelper.getAutocompleteService();
        autocompleteService.getPlacePredictions({ input: this.filterData.searchtext, language: "it", types: ["locality", "administrative_area_level_2"], componentRestrictions: { country: 'it' } }, async function (suggestions: any) {
            let placeSuggestions: any[] = [];

            if (suggestions) {
                let localities = suggestions.filter(x => x.types.some(y => y === "locality"));

                placeSuggestions.push(...localities.map(x => ({
                    description: x.description,
                    address: x.description,
                    type: "Place"
                })));

                let provinces = suggestions.filter(x => x.types.some(y => y === "administrative_area_level_2"));

                placeSuggestions.push(...provinces.map(x => ({
                    description: x.description,
                    address: x.description,
                    type: "Province"
                })));
            }

            const response = (await Axios.post(self.routes.WorkWithUs_GetSuggestionPlace, { searchtext: self.filterData.searchtext })).data;

            self.suggestions = [...response, ...placeSuggestions];
            self.suggestions = self.suggestions.map((obj, index) => ({ ...obj, index }));
        });
    }

    public setPlace() {
        if (this.suggestionIndex !== -1) {
            const suggestion = this.suggestions[this.suggestionIndex];
            if (suggestion.type !== "Place") {
                this.setMarker(suggestion);
            } else {
                this.selectSuggestion(suggestion);
            }
        }
    }

    public centerMarker(geoMarker: any) {
        this.googleMapsHelper.setActiveInfoWindowId(geoMarker.idMaster);
        this.googleMapsHelper.setCenter(geoMarker.latitude, geoMarker.longitude);
        this.googleMapsHelper.mapInstance.setZoom(17);
        this.googleMapsHelper.clickMarker(geoMarker.idMaster);
    }

    public setMarker(marker: any) {
        this.centerMarker(marker);
        this.filterData.searchtext = marker.company;
        this.resetSuggestionValues();
    }

    public async selectSuggestion(suggestion: any) {
        const self = this;
        this.mapErrors = [];

        if (suggestion.type === "Place") {
            this.googleMapsHelper.setActiveInfoWindowId(null);

            let geocoder = this.googleMapsHelper.getGeocoder();
            geocoder.geocode({ address: suggestion.address }, async function (data: any, status: any) {
                if (status === (<any>window).google.maps.GeocoderStatus.OK && data) {
                    let place = data[0];

                    if (place && !place.geometry) {
                        self.mapErrors.push("Indirizzo non trovato");
                        setTimeout(function () { self.mapErrors = []; }, 3000);
                        return;
                    } else {
                        let location = place.geometry.location.toJSON();

                        self.googleMapsHelper.mapInstance.setZoom(14);
                        self.googleMapsHelper.setCenter(location.lat, location.lng);

                        self.filterData.searchtext = place.formatted_address;
                    }
                }
            });
        } else {
            this.setMarker(suggestion);
        }

        this.resetSuggestionValues();
    }

    public resetSuggestionValues() {
        this.suggestionIndex = -1;
        this.suggestions = [];
    }

    public resetSearchtext() {
        this.filterData.searchtext = null;
        this.resetSuggestionValues();
    }

    public suggestionMoveDown() {
        if (this.suggestions.length !== 0 && this.suggestionIndex < this.suggestions.length - 1) {
            this.suggestionIndex++;
        }
    }

    public suggestionMoveUp() {
        if (this.suggestions.length !== 0 && this.suggestionIndex > 0) {
            this.suggestionIndex--;
        }
    }

    public async openDialog(jobOffer: any) {
        this.$router.push({ name: "JobOfferDetail", params: { offerIdMaster: jobOffer.idMaster } }).catch(() => { });
    }

    public async openSpontaneousCandidature() {
        this.$router.push({ name: "JobFreeProfileSubmit" }).catch(() => { });
    }

    public isValidationError(controlName: string): boolean {
        return FormHelper.isControlValidationError(controlName, this.validationErrors);
    }
}